import { CancelledError, GameError, NetworkError, SessionExpiredError, TimeoutError, ValidationError, kindForStatus, } from './errors.js';
import { ApiErrorBodySchema } from './schema.js';
export const CORRELATION_HEADER = 'X-Correlation-ID';
export class Transport {
    baseUrl;
    defaultTimeoutMs;
    fetchImpl;
    constructor(cfg) {
        this.baseUrl = cfg.baseUrl.replace(/\/+$/, '');
        this.defaultTimeoutMs = cfg.defaultTimeoutMs;
        // Bind to preserve `this` for the global implementation.
        const f = cfg.fetchImpl ?? globalThis.fetch;
        if (typeof f !== 'function') {
            throw new GameError('unknown', 'no fetch implementation available');
        }
        this.fetchImpl = f.bind(globalThis);
    }
    async request(opts) {
        const url = `${this.baseUrl}${opts.path}`;
        const timeoutMs = opts.timeoutMs ?? this.defaultTimeoutMs;
        const timeoutController = new AbortController();
        const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
        let timedOut = false;
        const onTimeout = () => {
            timedOut = true;
        };
        timeoutController.signal.addEventListener('abort', onTimeout, { once: true });
        const signal = mergeSignals(opts.signal, timeoutController.signal);
        const headers = { Accept: 'application/json', ...opts.headers };
        let bodyInit;
        if (opts.body !== undefined && opts.method === 'POST') {
            headers['Content-Type'] = 'application/json';
            bodyInit = JSON.stringify(opts.body);
        }
        let res;
        try {
            res = await this.fetchImpl(url, {
                method: opts.method,
                headers,
                ...(bodyInit !== undefined ? { body: bodyInit } : {}),
                signal,
                // Never attach ambient cookies/credentials; auth is via bearer token.
                credentials: 'omit',
                mode: 'cors',
            });
        }
        catch (err) {
            if (isAbortError(err)) {
                if (timedOut)
                    throw new TimeoutError();
                throw new CancelledError();
            }
            throw new NetworkError(err);
        }
        finally {
            clearTimeout(timer);
            timeoutController.signal.removeEventListener('abort', onTimeout);
        }
        const correlationId = res.headers.get(CORRELATION_HEADER) ?? undefined;
        if (!res.ok) {
            throw await this.toError(res, correlationId);
        }
        let json;
        try {
            json = await res.json();
        }
        catch (err) {
            throw new ValidationError('server returned a non-JSON or empty body', err);
        }
        const parsed = opts.schema.safeParse(json);
        if (!parsed.success) {
            throw new ValidationError(`server response failed validation: ${parsed.error.message}`);
        }
        return parsed.data;
    }
    async toError(res, correlationId) {
        let code;
        let message = `request failed with status ${res.status}`;
        let cid = correlationId;
        try {
            const body = ApiErrorBodySchema.safeParse(await res.json());
            if (body.success) {
                code = body.data.code;
                message = body.data.message;
                cid = body.data.correlation_id ?? cid;
            }
        }
        catch {
            // No/invalid JSON error body — keep the generic message.
        }
        if (res.status === 401) {
            return new SessionExpiredError(code, cid);
        }
        return new GameError(kindForStatus(res.status), message, {
            status: res.status,
            ...(code !== undefined ? { code } : {}),
            ...(cid !== undefined ? { correlationId: cid } : {}),
        });
    }
}
function isAbortError(err) {
    return err instanceof DOMException ? err.name === 'AbortError' : err?.name === 'AbortError';
}
/** Returns a signal that aborts when any input signal aborts. */
function mergeSignals(a, b) {
    if (!a)
        return b;
    if (a.aborted)
        return a;
    const merged = new AbortController();
    const onAbort = () => merged.abort();
    a.addEventListener('abort', onAbort, { once: true });
    b.addEventListener('abort', onAbort, { once: true });
    return merged.signal;
}
//# sourceMappingURL=http.js.map